import { useLocalSearchParams } from 'expo-router';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { ActivityIndicator, RefreshControl, StyleSheet } from 'react-native';

import { BackChevron, goBack } from '@/components/BackChevron';
import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Heading } from '@/components/Heading';
import { HeroWatermark } from '@/components/HeroWatermark';
import { Overline } from '@/components/Overline';
import { Screen } from '@/components/Screen';
import { TrackDetailsCard } from '@/components/social/TrackDetailsCard';
import { TrackTimeline } from '@/components/social/TrackTimeline';
import { WhileYouWaitCard } from '@/components/social/WhileYouWaitCard';
import { Stack } from '@/components/Stack';
import { Text } from '@/components/Text';
import { colors, fontSizes, spacing } from '@/constants/theme';
import { useDealActionPolling } from '@/hooks/useDealActionPolling';
import { useDealCta } from '@/hooks/useDealCta';
import { playbookReadable, playbookStep } from '@/lib/socialProgress';
import { dealsStore, isSettled } from '@/stores/deals.store';
import type { Action } from '@/types/deal';
import type { ShippingTracker } from '@/types/socialDeal';

/**
 * MarketPryce Social "Track package" (Figma "Track"): where the athlete's
 * free product is. Status, the timeline with each stage's time, the carrier
 * details, and the playbook while they wait. Everything is the deal page's
 * own Action (the product step's tracker and the order). Like the deal page,
 * useDealActionPolling syncs with Voltr on focus and polls while the deal is
 * open; a pull syncs again. Nothing here is worked out on the phone.
 */
const TrackPackageScreen = observer(() => {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const deal = slug ? dealsStore.bySlug(slug) : undefined;
  const action = deal ? dealsStore.actionFor(deal.id) : undefined;
  const { readBrief, ctaError } = useDealCta(deal, action);
  const [pullFailed, setPullFailed] = useState(false);
  const [pulling, setPulling] = useState(false);
  // A cold deep link: load the deals first; the polling hook takes it from there.
  useEffect(() => {
    void dealsStore.ensureLoaded();
  }, []);
  useDealActionPolling(deal?.id, isSettled(action));
  const failed = pullFailed || !!dealsStore.refreshError;

  const refresh = useCallback(async () => {
    setPullFailed(false);
    await dealsStore.ensureLoaded();
    const current = slug ? dealsStore.bySlug(slug) : undefined;
    if (current) await dealsStore.refreshAction(current.id, { sync: true }).catch(() => setPullFailed(true));
  }, [slug]);

  const onPull = useCallback(async () => {
    setPulling(true);
    await refresh();
    setPulling(false);
  }, [refresh]);

  const tracker = trackable(action);
  const back = () => goBack(slug ? `/deals/${slug}` : '/deals');

  if (!tracker) {
    return (
      <Screen edges={['top', 'bottom']}>
        <Stack gap="lg">
          <BackChevron onPress={back} label="View Deal" testID="track-back" />
          {emptyBody({ hasDeal: !!deal, failed, note: trackerOf(action)?.note ?? null, retry: () => void refresh() })}
        </Stack>
      </Screen>
    );
  }

  const current = tracker.stages.find((s) => s.current);
  const sample = action?.voltr?.sample;
  // "While you wait" only while it's on the way: once delivered there's nothing to wait for.
  const delivered = !!tracker.stages[tracker.stages.length - 1]?.reached;
  const playbook = !delivered && deal && playbookReadable(deal, action) ? playbookStep(deal) : undefined;

  return (
    <Screen
      edges={['top', 'bottom']}
      testID="track-package"
      refreshControl={<RefreshControl refreshing={pulling} onRefresh={() => void onPull()} tintColor={colors.primary} />}
    >
      <Stack gap="lg">
        <BackChevron onPress={back} label="View Deal" testID="track-back" />
        <Heading size="3xl">Track package</Heading>

        <Card variant="dark" style={styles.status} testID="track-status">
          <HeroWatermark />
          <Overline tone="onDark">Status</Overline>
          <Text variant="bodyBold" color="frost" style={styles.statusLabel}>
            {current?.label ?? ''}
          </Text>
          {sample?.product_name ? (
            <Text variant="bodySemiBold" color="electricGreen">
              {sample.product_name}
            </Text>
          ) : null}
        </Card>

        {failed ? (
          <Text variant="caption" color="textMuted" testID="track-stale">
            Couldn’t check for updates. Pull down to try again.
          </Text>
        ) : null}

        <TrackTimeline stages={tracker.stages} testID="track-timeline" />
        <TrackDetailsCard
          carrier={sample?.carrier}
          trackingNumber={sample?.tracking_number}
          url={tracker.tracking?.url}
          testID="track-details"
        />

        {playbook ? <WhileYouWaitCard onPress={() => void readBrief(playbook)} testID="track-playbook" /> : null}
        {ctaError ? (
          <Text variant="caption" color="danger" testID="track-playbook-error">
            {ctaError}
          </Text>
        ) : null}
      </Stack>
    </Screen>
  );
});

/** The product step's tracker: the one step the server gives a tracker to. */
function trackerOf(action: Action | undefined): ShippingTracker | undefined {
  return Object.values(action?.step_states ?? {}).find((entry) => entry.tracker)?.tracker;
}

/** The tracker once there is an order to follow; before that (or after a decline) there's nothing to track. */
function trackable(action: Action | undefined): ShippingTracker | undefined {
  const tracker = trackerOf(action);
  return tracker && !tracker.stages[0]?.current ? tracker : undefined;
}

type EmptyBodyArgs = { hasDeal: boolean; failed: boolean; note: string | null; retry: () => void };

/** Loading, a failed load, a deal that isn't here, or no order yet (with the brand's decline reason when there is one). */
function emptyBody({ hasDeal, failed, note, retry }: EmptyBodyArgs): ReactElement {
  if (failed || (!dealsStore.hasLoaded && dealsStore.error)) return <ErrorState onRetry={retry} testID="track-error" />;
  if (!dealsStore.hasLoaded || dealsStore.refreshing) return <ActivityIndicator color={colors.primary} />;
  if (!hasDeal) return <EmptyState title="Deal not found" body="This deal may have ended or isn't visible to you." testID="track-missing" />;
  return (
    <EmptyState
      title="Nothing to track yet"
      body={note ?? 'Once you order your product, you can follow it here.'}
      testID="track-empty"
    />
  );
}

const styles = StyleSheet.create({
  // The watermark sits partly outside the card; clip it to the corners.
  status: {
    gap: spacing.xs,
    padding: spacing.lg,
    overflow: 'hidden',
  },
  // The page's one big word ("Shipped"), Inter like the Figma, a step under the heading.
  statusLabel: {
    fontSize: fontSizes['2xl'],
    lineHeight: fontSizes['2xl'] + spacing.sm,
  },
});

export default TrackPackageScreen;
